import React, { useContext } from 'react';
import { path } from 'ramda';
import { getAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { AvenyCollectionMap } from '../../../../../../Model/ModelContext/ModelContext';
import { ValueType } from '../../../../../../Utils/Formatters/FormatterTypes';
import { attributeDefToDataPath } from '../../../../../../Utils/dataUtils';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import EditableAttribute from '../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { getAttributeValueAutoSize } from '../../../../../../Components/Attributes/Utils/AttributeValueAutoSizeUtils';
import {
  IBusinessDataFormContext,
  BusinessDataFormContext,
} from '../BusinessDataForm/BusinessDataForm';
import { IIntegratedContentName } from './IntegratedAssociationList';
import { ISuggestion } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import { ObjektVyFaltLista } from '../ObjektVyFaltLista/ObjektVyFaltLista';
import { ObjektLank } from '../ObjektLank/ObjektLank';
import { getAssociationLabel } from '../../../../../../Model/BusinessModelUtils';
import { businessDataFormValuesEqual } from '../BusinessDataForm/BusinessDataFormHasChanged/businessDataFormHasChanged';
import { EDInputGroup } from '../../../../../../BaseComponents/InputGroup/EDInputGroup';

interface IProps {
  contentAttributeNames: readonly IIntegratedContentName[];
  associatedCollection: IClientBusinessModel;
  associationName: string;
  modelData: INodeData;
  avenyCollectionMap: AvenyCollectionMap;
  data: INodeData; // { [key: string]: any };
  editedValues?: INodeData;
  onUpdate: (valueObject: INodeData) => void;
  onAssociationSelected: (
    suggestion: ISuggestion | null,
    associationName: string
  ) => void;
  marker?: boolean;
}

const IntegratedAssociationListItemContent: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const formContext = useContext<IBusinessDataFormContext>(
    BusinessDataFormContext
  );

  if (formContext == null) {
    return (
      <InternalError
        id="6b232a2d-8a44-4f45-a073-54ad3ef80c58"
        message=" IntegratedAssociationListItemContent utanför formcontext"
      />
    );
  }

  return (
    <ObjektVyFaltLista>
      <EDInputGroup>
        {props.contentAttributeNames.map((contentName, index) => {
          const name = getAssociationLabel(
            contentName,
            props.associatedCollection
          );
          if (
            (contentName.mode === 'read' && formContext.formState.editMode) ||
            (contentName.mode === 'edit' && !formContext.formState.editMode)
          ) {
            return null;
          }

          const validations = formContext.formState.validation.filter(
            validation =>
              typeof validation === 'object'
                ? validation.fieldName === contentName.name &&
                  validation.path?.toString().toLowerCase() ===
                    [props.associationName, props.data.id]
                      .toString()
                      .toLowerCase()
                : false
          );
          const errorMessage = validations
            .filter(val => val.type === 'Error')
            .map(val => val.message)
            .join(', ');
          const warningMessage = validations
            .filter(val => val.type === 'Warning')
            .map(val => val.message)
            .join(', ');
          const hintMessage = validations
            .filter(val => val.type === 'Hint')
            .map(val => val.message)
            .join(', ');

          if (contentName.association) {
            const nameSplit = contentName.name.split('.');
            const label = nameSplit[nameSplit.length - 1];
            return (
              <ObjektLank
                key={index}
                collection={props.associatedCollection}
                data={props.data}
                editedValues={props.editedValues}
                onAssociationSelected={suggestion =>
                  props.onAssociationSelected(suggestion, label)
                }
                size="large"
                associationName={contentName.name}
                label={name}
                id={
                  path(['_Associations', contentName.name, 'id'], props.data) ??
                  null
                }
                errorMessage={errorMessage}
                warningMessage={warningMessage}
                hintMessage={hintMessage}
              />
            );
          }

          const dataValue = path<ValueType>(
            attributeDefToDataPath(contentName.name),
            props.data
          );
          const editedValue = path<ValueType>(
            attributeDefToDataPath(contentName.name),
            props.editedValues
          );
          const value = editedValue ?? dataValue;

          const isEdited =
            editedValue !== undefined &&
            !businessDataFormValuesEqual(dataValue, editedValue);

          const attribute = getAttributeInfo(
            props.associatedCollection,
            contentName.name,
            props.avenyCollectionMap
          ).attribute;

          const onChange = (value: string) => {
            const newValueObject = {
              id: props.data.id,
              _version: props.data._version,
              [attribute.AttributeName]: value,
            };

            props.onUpdate(newValueObject);
          };
          return (
            <EditableAttribute
              key={index}
              collection={formContext.model}
              label={name}
              attribute={attribute}
              value={value}
              onChange={onChange}
              size={getAttributeValueAutoSize(attribute, 'medium')}
              editMode={formContext.formState.editMode}
              isInList
              required={attribute.Required}
              marker={isEdited}
              errorMessage={errorMessage}
              warningMessage={warningMessage}
              hintMessage={hintMessage}
            />
          );
        })}
      </EDInputGroup>
    </ObjektVyFaltLista>
  );
};

export default IntegratedAssociationListItemContent;
